import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { InputNode, InputSourceNode, MessagingTopology, OutputNode } from '@internal/open-catalog';
import { Button, createStyles, Dialog, DialogActions, DialogContent, DialogTitle, List, ListItem, ListItemText, makeStyles, TextField, Theme } from '@material-ui/core';
import { Autocomplete } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import useAsync from 'react-use/esm/useAsync';

export interface InputSourceDialogProps {
  open: boolean;
  environment: string;
  destination: InputNode;
  onClose: (newSource?: InputSourceNode) => void;
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    content: {
      paddingTop: 0,
      paddingBottom: 0
    },
    infoList: {
      '& > li': {
        padding: 0
      },
      '& > li > div': {
        maxWidth: '50%',
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1)
      },
      '& > li > div:first-child': {
        paddingLeft: 0
      },
      '& > li > div:last-child': {
        paddingRight: 0
      }
    },
    source: {
      overflow: 'unset',
      '& div[class|="MuiFormControl-root"]': {
        width: '100%'
      }
    }
  })
);

export function InputSourceDialog(props: InputSourceDialogProps) {

  const classes = useStyles();
  const { onClose, open, environment, destination } = props;

  const catalogApi = useApi(catalogApiRef);
  const [source, setSource] = useState<InputSourceNode>();
  const inputRef = useRef<HTMLInputElement>();

  const emptyState = () => {
    setSource(undefined);
  };

  const cancel = () => {
    onClose();
    emptyState();
  };

  const submit = () => {
    onClose(source);
    emptyState();
  };

  const { value: sources } = useAsync(async () => {
    const response = await catalogApi.getEntities({
      filter: {
        kind: 'Resource',
        ['spec.type']: 'Messaging',
        ['spec.environment']: environment
      }
    });
    return response.items.filter(mr => mr.spec)
      .map(mr => mr.spec?.topology as MessagingTopology)
      .filter(topology => topology.input?.name !== destination.name)
      .filter(topology => topology.output)
      .map(topology => topology.output!)
      .sort((a, b) => a.name.localeCompare(b.name)) as OutputNode[]
  }, [catalogApi, environment, destination]);

  const handleSourceChange = (
    event: React.SyntheticEvent<Element, Event>,
    value: OutputNode | null,
  ) => {
    const selected = sources?.find(s => s.name === (value ? value.name : ''));
    if (selected) {
      setSource({
        name: selected.name,
        type: selected.type,
        arguments: selected.arguments,
        bindings: [],
      });
    } else {
      setSource(undefined);
    }
  };

  useEffect(() => {
    setTimeout(() => inputRef?.current?.focus(), 100)
  }, [open, inputRef]);

  return (
    <Dialog open={open} fullWidth>
      <DialogTitle>New input source</DialogTitle>
      <DialogContent className={classes.content}>
        <List dense className={classes.infoList}>
          <ListItem>
            <ListItemText className={classes.source}>
              <Autocomplete
                options={sources ?? []}
                getOptionLabel={(option) => option.name}
                onChange={handleSourceChange}
                renderInput={(params) => (
                  <TextField
                    required
                    {...params}
                    label="Source"
                    variant="outlined"
                    size="small"
                    placeholder={source ? source.name : 'Select source...'}
                  />
                )}
                noOptionsText="No results found"
              />
            </ListItemText>
            <ListItemText
              primary="Destination"
              secondary={destination.name}
              title={destination.name}
            />
          </ListItem>
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={cancel} color="primary">
          Cancel
        </Button>
        <Button onClick={submit} color="secondary" disabled={!source}>
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};